import { useEffect, useMemo, useState } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';
import { resolveStoreName } from '@/lib/display';
import AnnonceCard from '@/components/feature/marketplace/AnnonceCard';
import MarketplaceFilters from '@/components/feature/marketplace/MarketplaceFilters';
import ProgressiveFooter from '@/components/feature/marketplace/ProgressiveFooter';
import { useProgressiveList } from '@/hooks/useProgressiveList';
import {
  fetchMarketplaceSellerIds,
  fetchMarketplaceCategories,
  fetchMarketplaceAnnonces,
  distinctCities,
  type MarketplaceAnnonce,
  type MarketplaceCategory,
} from '@/lib/marketplaceCatalog';

// ─────────────────────────────────────────────────────────────
// Page publique « Produits » ou « Services » des vendeurs de la
// marketplace mixte (même écran, discriminé par le type d'annonce).
// Filtre par titre, ville et catégorie.
// ─────────────────────────────────────────────────────────────

interface MarketplaceCatalogPageProps {
  annonceType: 'produits' | 'services';
}

export default function MarketplaceCatalogPage({ annonceType }: MarketplaceCatalogPageProps) {
  const { tenant, theme, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const storeName = resolveStoreName(tenant, theme);

  const isService = annonceType === 'services';
  const heading = isService ? 'Services' : 'Produits';
  const itemWord = isService ? 'service' : 'produit';

  const [annonces, setAnnonces] = useState<MarketplaceAnnonce[]>([]);
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');

  useEffect(() => {
    document.title = `${heading} — ${storeName}`;
  }, [heading, storeName]);

  useEffect(() => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sellerIds = await fetchMarketplaceSellerIds(merchantId);
        const [list, cats] = await Promise.all([
          fetchMarketplaceAnnonces(merchantId, { kind: annonceType, sellerIds }),
          fetchMarketplaceCategories(merchantId),
        ]);
        if (cancelled) return;
        setAnnonces(list);
        setCategories(cats);
      } catch {
        // silencieux
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId, annonceType]);

  const cities = useMemo(() => distinctCities(annonces), [annonces]);

  // Noms de catégories (id → nom) pour la chaîne affichée sur chaque carte.
  const categoryNameMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.nom])) as Record<number, string>,
    [categories],
  );

  // Catégories racines + sous-catégories de la catégorie active.
  const rootCategories = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  const subCategories = useMemo(
    () => (category ? categories.filter((c) => c.parent_id === Number(category)) : []),
    [categories, category],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Une catégorie racine inclut ses sous-catégories ; une sous-catégorie
    // sélectionnée restreint encore le résultat.
    let matchIds: Set<number> | null = null;
    if (subCategory) {
      matchIds = new Set<number>([Number(subCategory)]);
    } else if (category) {
      const childIds = categories.filter((c) => c.parent_id === Number(category)).map((c) => c.id);
      matchIds = new Set<number>([Number(category), ...childIds]);
    }
    return annonces.filter((a) => {
      if (q && !(a.titre || '').toLowerCase().includes(q)) return false;
      if (city && (a.ville || '').trim() !== city) return false;
      if (matchIds) {
        if (a.idcategorie == null || !matchIds.has(Number(a.idcategorie))) return false;
      }
      return true;
    });
  }, [annonces, query, city, category, subCategory, categories]);

  const pager = useProgressiveList(filtered, 12);

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-3 ${
              isService ? 'bg-secondary-100 text-secondary-900' : 'bg-primary-100 text-primary-700'
            }`}
          >
            <i className={isService ? 'ri-service-line' : 'ri-shopping-bag-3-line'}></i>
            Marketplace
          </span>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
            {heading} des vendeurs
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            Découvrez les {itemWord}s proposés par les vendeurs de {storeName}.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <MarketplaceFilters
              query={query}
              onQueryChange={setQuery}
              city={city}
              onCityChange={setCity}
              cities={cities}
              category={category}
              onCategoryChange={(v) => { setCategory(v); setSubCategory(''); }}
              subCategory={subCategory}
              onSubCategoryChange={setSubCategory}
              categories={rootCategories}
              subCategories={subCategories}
              placeholder={`Rechercher un ${itemWord} par titre…`}
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className={`${isService ? 'ri-service-line' : 'ri-shopping-bag-3-line'} text-2xl text-foreground-400`}></i>
              </div>
              <p className="text-foreground-500">Aucun {itemWord} ne correspond à votre recherche</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-foreground-400 mb-4">
                {filtered.length} {itemWord}{filtered.length > 1 ? 's' : ''} trouvé{filtered.length > 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
                {pager.visible.map((a) => (
                  <AnnonceCard key={a.id} annonce={a} monaie={monaie} categoryNames={categoryNameMap} />
                ))}
              </div>
              <ProgressiveFooter
                hasMore={pager.hasMore}
                shown={pager.visible.length}
                total={pager.total}
                remaining={pager.remaining}
                loadMore={pager.loadMore}
                sentinelRef={pager.sentinelRef}
                label={`${itemWord}s`}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}